/**
 * SaveLibraryDialog — модалка для сохранения текущей формы как Library.
 *
 * P1.1 / P1.5 2026-07-28:
 * - Убран description (UI + state + payload) — сохранение только по имени.
 * - Self-rolled backdrop заменён на общий <Modal> с click-outside + Esc + body lock.
 *
 * Block L2.1 (TZ 2026-07-29):
 * - Принимает `projectId` и пробрасывает в `create`. Если `projectId` не задан
 *   — library створюється personal (project_id=null). LibraryBar подає
 *   currentProjectId зі store.
 */
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from '@/components/ui/Modal';
import { useCreateLibrary } from '@/hooks/useLibraries';
import type { LibraryKindT } from './LibraryBar';

export interface SaveLibraryDialogProps {
  open: boolean;
  onClose: () => void;
  /** P1.4: null = unified workspace library (kind field omitted on create). */
  kind: LibraryKindT | null;
  /** Возвращает текущий snapshot из store state. */
  onBuildSnapshot: () => Record<string, unknown>;
  /** Block L2.1: поточний проект (для project-scoped libraries). */
  projectId?: string | null;
}

export function SaveLibraryDialog({ open, onClose, kind, onBuildSnapshot, projectId }: SaveLibraryDialogProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const create = useCreateLibrary();

  useEffect(() => {
    if (open) {
      setName('');
      setErrors([]);
    }
  }, [open]);

  const trimmedName = name.trim();
  const disabled = trimmedName.length === 0 || create.isPending;

  async function submit() {
    if (disabled) return;
    try {
      // P1.4: kind === null → unified workspace library (backend трактует null
      // как unified). kind !== null → legacy per-kind, шлём как раньше.
      await create.mutateAsync({
        kind,
        name: trimmedName,
        description: null,
        project_id: projectId ?? null,
        snapshot: onBuildSnapshot(),
      });
      onClose();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: unknown } } };
      const detail = err?.response?.data?.detail;
      if (Array.isArray(detail)) {
        setErrors(detail.map((d) => (typeof d === 'object' && d && 'msg' in d ? String((d as { msg: unknown }).msg) : String(d))));
      } else if (typeof detail === 'string') {
        setErrors([detail]);
      } else {
        setErrors([t('libraries.errors.saveFailed', { defaultValue: 'Save failed' })]);
      }
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('libraries.saveDialogTitle', { defaultValue: 'Save as library' })}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose} disabled={create.isPending}>
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </button>
          <button
            className="btn-primary"
            onClick={submit}
            disabled={disabled}
          >
            {create.isPending
              ? t('libraries.saving', { defaultValue: 'Saving…' })
              : t('libraries.save', { defaultValue: 'Save' })}
          </button>
        </>
      }
    >
      <p className="text-xs text-surface-400 mb-4">
        {t('libraries.saveDialogSubtitle', {
          defaultValue: 'Save current form as a reusable library.',
        })}
      </p>

      <div className="space-y-3">
        <div>
          <label className="text-xs text-surface-500 uppercase tracking-wider">
            {t('libraries.name', { defaultValue: 'Name' })}
          </label>
          <input
            className="input w-full mt-1"
            autoFocus
            maxLength={120}
            placeholder={t('libraries.namePlaceholder', {
              defaultValue: 'e.g. Adidas enrichment — economy',
            })}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submit();
            }}
          />
        </div>

        {errors.length > 0 && (
          <ul className="text-xs text-danger list-disc pl-4 space-y-0.5">
            {errors.map((m, i) => (
              <li key={i}>{m}</li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}
